import { CommonModule } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';

/**
 * Presentational cell that renders matter information consistently across the
 * app: an optional client name (small grey line), the matter name (link line)
 * and the client-matter number (monospace line).
 *
 * The component owns no navigation or permission logic. Parents decide how the
 * cell behaves:
 * - Provide {@link routerLink} (and optional {@link linkState}) to render a
 *   declarative router link.
 * - Set {@link link} to `true` to render a clickable cell that emits
 *   {@link clicked} with the matter number (imperative navigation).
 * - Leave both unset to render a static, non-interactive cell.
 *
 * Set {@link loading} to render skeleton placeholder lines.
 */
@Component({
  selector: 'app-matter-cell',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './matter-cell.component.html',
  styleUrls: ['./matter-cell.component.scss'],
})
export class MatterCellComponent {
  /** Client-matter number, e.g. "00894-35267". Emitted on click. */
  readonly matterNumber = input.required<string>();

  /** Matter title shown on the middle line. */
  readonly matterName = input<string | null | undefined>();

  /** Optional client name shown on the top line. Omit to hide the line. */
  readonly clientName = input<string | null | undefined>();

  /** Text shown on the matter-name line when {@link matterName} is empty. */
  readonly matterNameFallback = input('Matter');

  /** Render a clickable cell that emits {@link clicked}. Ignored when {@link routerLink} is set. */
  readonly link = input(false);

  /** Render a declarative router link target. Takes precedence over {@link link}. */
  readonly routerLink = input<unknown[] | string | null | undefined>();

  /** Router navigation state, forwarded to `[state]`. Only used with {@link routerLink}. */
  readonly linkState = input<{ [k: string]: unknown } | undefined>();

  /** Render skeleton placeholder lines instead of content. */
  readonly loading = input(false);

  /** Emits the matter number when an imperative-link cell is clicked. */
  readonly clicked = output<string>();

  onClick(): void {
    this.clicked.emit(this.matterNumber());
  }
}
